import { DisplayAttachmentHistory } from '@/components/attachment-uploader/display-attachment-history'
import { monthOptions, yearOptions } from '@/components/attachment-uploader/utils'
import { FileInput } from '@/shared/components/file-input'
import { toOptions } from '@/utils'
import { DOCUMENT_UPLOAD_ACCEPT, validateDocumentFile } from '@/utils/document-upload'
import { Input, Typography } from '@material-tailwind/react'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomSelect from '@/shared/components/custom-select'
import { FileInputSection } from './attachment-uploader.styled'
import { ATTACHMENT_STATUSES } from '@/constants'
import moment from '@/config/moment'
import { toast } from 'react-toastify'
import { FiX } from 'react-icons/fi'

const initialStates = Object.freeze({
  file: {
    data: '',
    filename: '',
    content_type: '',
    identify: false
  }
})

export const AttachmentUploader = (props) => {
  const {
    attachmentTypes,
    attachableType,
    onUpload,
    children,
    showLatestAttachments,
    attachments,
    initialValues,
    showDefaultHeader,
    isDisabled,
    restrictPastExpirationDates,
    expirationDateNotice,
    replacementModeEnabled,
    replacementEffectiveOnMax
  } = props
  const { t } = useTranslation()
  const minExpirationDate = useMemo(() => {
    if (!restrictPastExpirationDates) return undefined

    return moment().format('YYYY-MM-DD')
  }, [restrictPastExpirationDates])

  const latestAttachments = useMemo(() => {
    return attachments.filter(attachment => attachment.status !== 'pending_upload').slice(0, 5)
  }, [attachments])

  // memos
  const filteredAttachmentTypes = useMemo(() => {
    if (!attachableType) return attachmentTypes

    return attachmentTypes.filter((attachmentType) => {
      const klass = attachmentType?.klass || attachmentType?.resource?.klass
      return klass === attachableType
    })
  }, [attachmentTypes, attachableType])

  const attachmentTypeOptions = useMemo(() => {
    return toOptions({ data: filteredAttachmentTypes })
  }, [filteredAttachmentTypes])

  // formik
  const {
    values,
    setFieldValue,
    handleChange,
    handleSubmit,
    resetForm
  } = useFormik({ initialValues, onSubmit: onUpload, enableReinitialize: true })
  const showExpirationWarning = useMemo(() => {
    if (!expirationDateNotice) return false
    if (!values.expiration_date) return false

    const selectedDate = moment(values.expiration_date, 'YYYY-MM-DD', true)
    if (!selectedDate.isValid()) return false

    const daysUntilExpiration = selectedDate.startOf('day').diff(moment().startOf('day'), 'days')
    return daysUntilExpiration < 7
  }, [expirationDateNotice, values.expiration_date])

  // states
  const [selectedAttachmentType, setSelectedAttachmentType] = useState(
    attachmentTypeOptions.find(({ value }) => value === values.attachment_type_id) || {}
  )

  const [file, setFile] = useState(initialStates.file)
  const [isNotApplicableLocked, setIsNotApplicableLocked] = useState(false)
  const hasHandledSelectedAttachmentType = useRef(false)

  const selectedAttachmentTypeResource = selectedAttachmentType?.resource || {}
  const expirationDateEnabled = selectedAttachmentTypeResource.recurrence === 'once' &&
    selectedAttachmentTypeResource.expirationDateEnabled === true
  const expirationDateRequired = expirationDateEnabled &&
    selectedAttachmentTypeResource.expirationDateRequired === true
  const showClearExpirationDate = expirationDateEnabled &&
    !expirationDateRequired &&
    !!values.expiration_date

  useEffect(() => {
    if (!selectedAttachmentType.value) {
      setIsNotApplicableLocked(false)
      return
    }
    setFile(initialStates.file)

    if (selectedAttachmentType.resource.recurrence === 'monthly') {
      if (!values.period_month) return
      if (!values.period_year) return
    }

    if (selectedAttachmentType.resource.recurrence === 'yearly') {
      if (!values.period_year) return
    }

    const [currentAttached = {}] = attachments.filter(attachment => {
      return attachment.periodMonth === values.period_month &&
        attachment.periodYear === values.period_year &&
        attachment.attachmentType.id === values.attachment_type_id
    })

    if (!currentAttached.id) {
      setIsNotApplicableLocked(false)
      return
    }

    const isLocked = currentAttached.status === ATTACHMENT_STATUSES.notApplicable
    setIsNotApplicableLocked(isLocked)

    const newFile = {
      data: currentAttached.documentInfo.data,
      filename: currentAttached.documentInfo.filename,
      content_type: currentAttached.documentInfo.content_type,
      identify: false
    }

    setFieldValue('id', currentAttached.id)
    const shouldDropExpiration =
      currentAttached.status &&
      !['pending', 'pending_upload'].includes(currentAttached.status)
    setFieldValue(
      'expiration_date',
      shouldDropExpiration || !expirationDateEnabled ? null : currentAttached.expirationDate
    )
    setFieldValue(
      'status',
      isLocked ? ATTACHMENT_STATUSES.notApplicable : ATTACHMENT_STATUSES.pending
    )
    setFile(newFile)
  }, [
    values.period_month,
    values.period_year,
    values.attachment_type_id,
    expirationDateEnabled
  ])

  useEffect(() => {
    if (!selectedAttachmentType.value) return

    const shouldPreserveInitialExpirationDate = !hasHandledSelectedAttachmentType.current
    hasHandledSelectedAttachmentType.current = true

    resetForm()
    setFieldValue('attachment_type_id', selectedAttachmentType.value)
    setFieldValue(
      'expiration_date',
      shouldPreserveInitialExpirationDate ? initialValues.expiration_date || null : null
    )
    setFile(initialStates.file)
    setIsNotApplicableLocked(false)
  }, [selectedAttachmentType])

  useEffect(() => {
    setFieldValue('document', file)
  }, [file])

  const handleSubmitWithGuard = () => {
    if (isNotApplicableLocked) return

    if (
      replacementModeEnabled &&
      values.replacement_effective_mode === 'scheduled' &&
      values.replacement_effective_on &&
      replacementEffectiveOnMax &&
      values.replacement_effective_on > replacementEffectiveOnMax
    ) {
      toast.warn('La fecha de entrada en vigencia no puede ser posterior al dia siguiente del vencimiento del documento vigente.')
      return
    }

    if (
      restrictPastExpirationDates &&
      expirationDateEnabled &&
      values.expiration_date &&
      values.expiration_date < minExpirationDate
    ) {
      toast.warn(t('common.uploadDocuments.invalidExpirationDate'))
      return
    }

    if (expirationDateRequired && !values.expiration_date) {
      toast.warn('Ingresa la fecha de vencimiento del documento.')
      return
    }

    handleSubmit()
  }

  return (
    <form>
      <div className='flex flex-col gap-6'>
        {
          showDefaultHeader &&
          <div className='flex flex-col gap-6'>
            <section className='flex flex-col'>
              <Typography
                variant='h4'
                className='text-gray-900 font-semibold not-italic'
              >
                Cargador de documentos
              </Typography>

              <Typography variant='paragraph' className='text-gray-500'>
                Selecciona y sube o sobrescribe los documentos.
              </Typography>
            </section>
          </div>
        }

        <CustomSelect
          placeholder='Selecciona un documento'
          options={attachmentTypeOptions}
          onChange={setSelectedAttachmentType}
          value={selectedAttachmentType}
          isLoading={!attachmentTypes.length}
          isDisabled={isDisabled}
        />
        <div className='grid grid-cols-2 gap-4'>
          {

            selectedAttachmentType?.resource?.recurrence === 'monthly' &&
            <CustomSelect
              isDisabled={!selectedAttachmentType.value || isDisabled}
              placeholder='Selecciona un mes'
              options={monthOptions}
              onChange={({ value }) => setFieldValue('period_month', value)}
              value={monthOptions.find(({ value }) => value === values.period_month) || ''}
            />
          }

          {
            ['monthly', 'yearly'].includes(selectedAttachmentType?.resource?.recurrence) &&
            <CustomSelect
              isDisabled={!selectedAttachmentType.value || isDisabled}
              placeholder='Selecciona un año'
              options={yearOptions()}
              onChange={({ value }) => setFieldValue('period_year', value)}
              value={yearOptions().find(({ value }) => value === values.period_year) || ''}
            />
          }
        </div>

        <section className='grid grid-cols-12 gap-4'>
          <FileInputSection fullWidth={!showLatestAttachments}>
            <Typography
              variant='paragraph'
              className='font-semibold text-gray-900'
            >
              Nuevo archivo
            </Typography>
            {
              isNotApplicableLocked
                ? (
                  <Typography variant='small' className='text-gray-600'>
                    Este documento está marcado como no aplica. No se puede subir un archivo.
                  </Typography>
                  )
                : (
                  <FileInput
                    className='flex flex-col items-center justify-center'
                    handleChange={setFile}
                    value={file}
                    accept={DOCUMENT_UPLOAD_ACCEPT}
                    validateFile={validateDocumentFile}
                    onInvalidFile={(message) => toast.warn(message)}
                  />
                  )
            }
          </FileInputSection>

          {
            showLatestAttachments &&
            <article className='col-span-4 flex flex-col gap-4'>
              <Typography
                variant='paragraph'
                className='font-semibold text-gray-900 p-0'
              >
                Últimos archivos cargados
              </Typography>
              <DisplayAttachmentHistory attachments={latestAttachments} />
            </article>
          }

          {expirationDateEnabled && (
            <article className='col-span-12 flex flex-col gap-5'>
              <Typography
                variant='paragraph'
                className='font-semibold text-gray-900'
              >
                {t('common.uploadDocuments.expirationDate')}{expirationDateRequired ? ' *' : ''}
              </Typography>
              <div className='flex flex-col gap-4 md:flex-row md:items-start'>
                <div className='md:w-72 md:flex-none'>
                  <div className='flex items-start gap-2'>
                    <div className='flex-1'>
                      <Input
                        name='expiration_date'
                        type='date'
                        value={values.expiration_date || ''}
                        label='Seleccione una fecha'
                        onChange={handleChange}
                        min={minExpirationDate}
                      />
                    </div>
                    {showClearExpirationDate && (
                      <button
                        type='button'
                        className='mt-1 flex h-9 w-9 flex-none items-center justify-center rounded-full border border-gray-400 bg-white text-gray-900 shadow-sm transition hover:border-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
                        aria-label='Quitar fecha de vencimiento'
                        title='Quitar fecha de vencimiento'
                        onClick={() => setFieldValue('expiration_date', '')}
                      >
                        <FiX className='h-5 w-5 stroke-[2.5]' />
                      </button>
                    )}
                  </div>
                  {expirationDateRequired && (
                    <Typography variant='small' className='mt-1 text-xs text-red-700'>
                      La fecha de vencimiento es obligatoria.
                    </Typography>
                  )}
                </div>
                {showExpirationWarning && (
                  <Typography variant='small' className='text-red-700 md:flex-1'>
                    {expirationDateNotice}
                  </Typography>
                )}
              </div>
            </article>
          )}

          {replacementModeEnabled && (
            <article className='col-span-12 flex flex-col gap-4'>
              <Typography
                variant='paragraph'
                className='font-semibold text-gray-900'
              >
                Vigencia del reemplazo
              </Typography>
              <div className='flex flex-col gap-3'>
                <label className='flex items-center gap-2 text-sm text-gray-800'>
                  <input
                    type='radio'
                    name='replacement_effective_mode'
                    checked={(values.replacement_effective_mode || 'immediate') === 'immediate'}
                    onChange={() => {
                      setFieldValue('replacement_effective_mode', 'immediate')
                      setFieldValue('replacement_effective_on', '')
                    }}
                  />
                  Reemplazar inmediatamente al ser aprobado
                </label>

                <label className='flex items-center gap-2 text-sm text-gray-800'>
                  <input
                    type='radio'
                    name='replacement_effective_mode'
                    checked={values.replacement_effective_mode === 'scheduled'}
                    onChange={() => setFieldValue('replacement_effective_mode', 'scheduled')}
                  />
                  Programar entrada en vigencia
                </label>

                {values.replacement_effective_mode === 'scheduled' && (
                  <div className='md:w-72 md:flex-none'>
                    <Input
                      name='replacement_effective_on'
                      type='date'
                      value={values.replacement_effective_on || ''}
                      label='Fecha de entrada en vigencia'
                      onChange={handleChange}
                      min={moment().format('YYYY-MM-DD')}
                      max={replacementEffectiveOnMax}
                    />
                  </div>
                )}
              </div>
            </article>
          )}
        </section>

        {children(handleSubmitWithGuard)}
      </div>
    </form>
  )
}

AttachmentUploader.propTypes = {
  attachmentTypes: PropTypes.array,
  attachableType: PropTypes.string,
  showLatestAttachments: PropTypes.bool,
  attachments: PropTypes.array,
  onUpload: PropTypes.func,
  children: PropTypes.node,
  initialValues: PropTypes.object,
  showDefaultHeader: PropTypes.bool,
  isDisabled: PropTypes.bool,
  restrictPastExpirationDates: PropTypes.bool,
  expirationDateNotice: PropTypes.string,
  replacementModeEnabled: PropTypes.bool,
  replacementEffectiveOnMax: PropTypes.string
}

AttachmentUploader.defaultProps = {
  showLatestAttachments: false,
  attachments: [],
  attachmentTypes: [],
  attachableType: null,
  initialValues: {
    id: null,
    attachment_type_id: null,
    document: {},
    expiration_date: null,
    period_month: null,
    period_year: null,
    status: 'pending',
    replacement_effective_mode: 'immediate',
    replacement_effective_on: null
  },
  showDefaultHeader: true,
  isDisabled: false,
  restrictPastExpirationDates: false,
  expirationDateNotice: null,
  replacementModeEnabled: false,
  replacementEffectiveOnMax: undefined
}
